import type * as THREE from 'three'
import type {
  TopologyFeatureFaceGeometryProvider,
  TopologyFeatureFaceGeometryProviderInput,
} from '../../mesh/feature'
import { ImportedTopologyFaceGeometryProvider } from './ImportedTopologyFaceGeometryProvider'
import type { ImportedMeshTopologyBinding } from './ImportedMeshTopologyBinding'
import { mergeOwnedFaceGeometries } from '../../mesh/utils'

/** 默认最多保留的不可变面几何数量。 */
const DEFAULT_MAX_CACHED_FACE_GEOMETRIES = 16
/** 默认面几何缓存字节预算：8 MiB。 */
const DEFAULT_MAX_CACHED_FACE_GEOMETRY_BYTES = 8 * 1024 * 1024
/** BVH 按原始几何属性字节的两倍做保守估算。 */
const FACE_GEOMETRY_BVH_ESTIMATED_BYTE_MULTIPLIER = 2

/**
 * 同一导入 Body 多显示 Mesh 的组合面几何 Provider。
 *
 * 设计说明：
 * 1. 多材质 GLB 会把一个 CAD Body 拆成多个 visible Mesh，但权威拓扑仍然只有一份；
 * 2. 每个单 Mesh Provider 只解析自己持有的 face -> triangleRange 映射；
 * 3. 合并全部材质内的面片；任何必要片段无法按同一坐标系重建时，统一回退完整 Body 几何。
 */
export class ImportedTopologyCompositeFaceGeometryProvider implements TopologyFeatureFaceGeometryProvider {
  /** 按显示 Mesh 注册顺序排列的单 Mesh Provider。 */
  private readonly providers: TopologyFeatureFaceGeometryProvider[]
  /** 按最近使用顺序保存的不可变面几何。 */
  private readonly entriesByKey = new Map<TopologySnapshotFace, ImportedTopologyFaceGeometryCacheEntry>()
  /** 支持 FeatureFace 释放时按几何引用归还所有权。 */
  private readonly entriesByGeometry = new Map<THREE.BufferGeometry, ImportedTopologyFaceGeometryCacheEntry>()
  /** 缓存几何数量上限。 */
  private readonly maxCachedGeometries: number
  /** 缓存几何字节上限。 */
  private readonly maxCachedBytes: number
  /** 当前缓存几何的估算总字节数。 */
  private cachedBytes = 0
  /** 累计缓存命中次数。 */
  private cacheHits = 0
  /** 累计实际几何构建次数。 */
  private cacheMisses = 0
  /** 累计预算淘汰次数。 */
  private evictions = 0
  /** 生命周期状态。 */
  private disposed = false

  /**
   * 创建组合面几何 Provider。
   *
   * @param bindings 同一 Body occurrence 的全部显示 Mesh 拓扑绑定。
   * @param options 数量与字节双预算；活动代理引用不被提前淘汰。
   */
  public constructor(
    private readonly bindings: readonly ImportedMeshTopologyBinding[],
    options: ImportedTopologyFaceGeometryCacheOptions = {},
  ) {
    this.providers = bindings.map((binding) => new ImportedTopologyFaceGeometryProvider(binding))
    this.maxCachedGeometries = Math.max(
      0,
      Math.floor(options.maxCachedGeometries ?? DEFAULT_MAX_CACHED_FACE_GEOMETRIES),
    )
    this.maxCachedBytes = Math.max(
      0,
      Math.floor(options.maxCachedBytes ?? DEFAULT_MAX_CACHED_FACE_GEOMETRY_BYTES),
    )
  }

  /**
   * 从同一 Body 的全部显示 Mesh 中解析指定拓扑面的显示几何。
   *
   * @param input 面代理几何构建输入。
   * @returns 所有显式片段合并后的独占几何；无法完整构建时返回 `null`。
   */
  public createFaceGeometry(input: TopologyFeatureFaceGeometryProviderInput): THREE.BufferGeometry | null {
    if (this.disposed) {
      throw new Error('导入拓扑面几何 Provider 已释放，不能继续创建代理几何')
    }

    /** 同一 topology snapshot 内的 face 对象身份稳定，热态命中不构造字符串或范围数组。 */
    const key = input.face
    const cachedEntry = this.entriesByKey.get(key)
    if (cachedEntry) {
      cachedEntry.activeReferences += 1
      this.touchEntry(cachedEntry)
      this.cacheHits += 1
      return cachedEntry.geometry
    }

    const geometry = this.createOwnedFaceGeometry(input)
    if (!geometry) return null

    const entry: ImportedTopologyFaceGeometryCacheEntry = {
      key,
      geometry,
      activeReferences: 1,
      estimatedBytes: this.estimateGeometryBytes(geometry),
    }
    this.entriesByKey.set(key, entry)
    this.entriesByGeometry.set(geometry, entry)
    this.cachedBytes += entry.estimatedBytes
    this.cacheMisses += 1
    this.evictIdleEntries()
    return geometry
  }

  /**
   * 归还 FeatureFace 对缓存几何的活动引用。
   *
   * @param geometry 即将脱离代理的几何。
   * @returns 几何由当前 Provider 持有时返回 `true`。
   */
  public releaseFaceGeometry(geometry: THREE.BufferGeometry): boolean {
    const entry = this.entriesByGeometry.get(geometry)
    if (!entry) return false
    if (entry.activeReferences > 0) entry.activeReferences -= 1
    this.touchEntry(entry)
    this.evictIdleEntries()
    return true
  }

  /** @returns 只在显式性能检查时创建的缓存统计。 */
  public getCacheStats(): ImportedTopologyFaceGeometryCacheStats {
    let activeReferences = 0
    this.entriesByKey.forEach((entry) => {
      activeReferences += entry.activeReferences
    })
    return {
      cachedGeometries: this.entriesByKey.size,
      cachedBytes: this.cachedBytes,
      activeReferences,
      cacheHits: this.cacheHits,
      cacheMisses: this.cacheMisses,
      evictions: this.evictions,
    }
  }

  /** 释放全部 Provider 所有的缓存几何，解除源 Mesh 引用。 */
  public dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.entriesByKey.forEach((entry) => entry.geometry.dispose())
    this.entriesByKey.clear()
    this.entriesByGeometry.clear()
    this.cachedBytes = 0
    /** 当前单 Mesh Provider 尚无自有缓存；保留标准释放委托，允许未来实现扩展而无需修改组合层。 */
    this.providers.forEach((provider) => provider.dispose?.())
  }

  /**
   * 从同一 Body 的全部显示 Mesh 构建一份新的独占几何。
   *
   * @param input 面代理几何构建输入。
   * @returns 新建几何；缺少完整显式映射时返回 `null`。
   */
  private createOwnedFaceGeometry(
    input: TopologyFeatureFaceGeometryProviderInput,
  ): THREE.BufferGeometry | null {
    const geometries: THREE.BufferGeometry[] = []
    try {
      for (let index = 0; index < this.providers.length; index += 1) {
        if (this.bindings[index]!.resolveFaceTriangleRanges(input.face).length === 0) continue
        const geometry = this.providers[index]!.createFaceGeometry(input)
        if (!geometry) {
          geometries.forEach(part => part.dispose())
          return null
        }
        geometries.push(geometry)
      }
    } catch (error) {
      geometries.forEach(geometry => geometry.dispose())
      throw error
    }
    return mergeOwnedFaceGeometries(geometries)
  }

  /**
   * 将命中或刚释放的条目移动到 LRU 队尾。
   *
   * @param entry 当前缓存条目。
   */
  private touchEntry(entry: ImportedTopologyFaceGeometryCacheEntry): void {
    this.entriesByKey.delete(entry.key)
    this.entriesByKey.set(entry.key, entry)
  }

  /**
   * 在不销毁活动代理几何的前提下执行数量与字节双预算淘汰。
   */
  private evictIdleEntries(): void {
    while (
      this.entriesByKey.size > this.maxCachedGeometries
      || this.cachedBytes > this.maxCachedBytes
    ) {
      let candidate: ImportedTopologyFaceGeometryCacheEntry | null = null
      for (const entry of this.entriesByKey.values()) {
        if (entry.activeReferences === 0) {
          candidate = entry
          break
        }
      }
      if (!candidate) return
      this.entriesByKey.delete(candidate.key)
      this.entriesByGeometry.delete(candidate.geometry)
      this.cachedBytes -= candidate.estimatedBytes
      candidate.geometry.dispose()
      this.evictions += 1
    }
  }

  /**
   * 估算几何属性、索引数组与 BVH 字节数，同一底层数组只计算一次。
   *
   * @param geometry 缓存几何。
   * @returns 用于预算比较的字节数，至少为 1。
   */
  private estimateGeometryBytes(geometry: THREE.BufferGeometry): number {
    const arrays = new Set<ArrayBufferView>()
    Object.values(geometry.attributes).forEach((attribute) => {
      const array = Reflect.get(attribute, 'array')
        ?? Reflect.get(Reflect.get(attribute, 'data') ?? {}, 'array')
      if (ArrayBuffer.isView(array)) arrays.add(array)
    })
    const indexArray = Reflect.get(geometry.getIndex() ?? {}, 'array')
    if (ArrayBuffer.isView(indexArray)) arrays.add(indexArray)
    let bytes = 0
    arrays.forEach((array) => {
      bytes += array.byteLength
    })
    return Math.max(1, bytes * FACE_GEOMETRY_BVH_ESTIMATED_BYTE_MULTIPLIER)
  }
}
